import { ReparoApi } from './index';
import type { PaymentAnchor, PaymentTrancheType } from './mother-order';

export type CreateVendorPayload = {
    /**
     * The vendor's code, which is also its primary key and the join key across
     * purchase orders, vendor-material mappings and lanes. Not editable afterwards.
     */
    vendorId: string;
    name: string;
    type: string;
    street?: string;
    city?: string;
    country?: string;
    zip?: string;
    email?: string;
    /**
     * The registered company, when it differs from what everyone calls them.
     *
     * `name` is the working name a buyer types ("Apricot"); this is who the paperwork is
     * actually with ("Apricot Foods Private Limited"). A production order prints this one,
     * because that is the party the GSTIN and the address belong to.
     */
    legalName?: string;
    /** GSTIN. Stored on the vendor as `tax_code`, and what a production order prints. */
    taxCode?: string;
    incoterms?: string;
    paymentTerms?: string;
    moq?: number;
    capabilities?: VendorCapability[];
    /**
     * One of the brand's purchase-order prefixes (Settings > Order Numbering). Picking this
     * vendor on a new order fills the number from it. Null = the order's picker decides.
     */
    poNumberPrefix?: string | null;
};

/**
 * A vendor as the API returns it, which is the whole row rather than a stub.
 *
 * The address fields are here because both `POST /vendors` and `GET /vendors/:id` already send
 * them — the type was simply narrower than the payload. A production order seeds its
 * manufacturer block from these, so widening the type saved a second round trip after a create.
 */
export type Vendor = {
    vendorId: string;
    name: string;
    type?: string;
    legalName?: string | null;
    email?: string;
    street?: string;
    city?: string;
    country?: string;
    zip?: string;
    taxCode?: string;
    capabilities?: VendorCapability[] | null;
    poNumberPrefix?: string | null;
};

/**
 * One vendor, in full.
 *
 * The list endpoint deliberately does NOT carry street, zip or taxCode - it feeds several
 * grids, and widening it would grow every one of their payloads to serve this one form. A
 * single fetch on select costs one small request at the moment somebody actually needs it.
 */
export const getVendor = async (vendorId: string): Promise<Vendor> =>
    ReparoApi<Vendor>({
        method: 'GET',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
    });

/**
 * One vendor's lanes: where it ships and how long each leg takes. The four components sum
 * to the door-to-door promise a Complete-by date can be seeded from.
 */
export type VendorLane = {
    laneId?: string;
    warehouseId: string;
    source?: string | null;
    destination?: string | null;
    mode?: string | null;
    productionTime?: number | null;
    qcTime?: number | null;
    transitTime?: number | null;
    deliveryTime?: number | null;
    /** Container cap for this leg in CBM. Null = no cap. */
    maxCbmPerShipment?: number | null;
    /** How full a container should be before it is worth sending, % of the cap. Null = no opinion. */
    minFillPct?: number | null;
};

export const getVendorLanes = async (vendorId: string): Promise<VendorLane[]> =>
    ReparoApi<VendorLane[]>({
        method: 'GET',
        url: `/vendors/${encodeURIComponent(vendorId)}/lanes`,
    });

/** What the vendor page may set on a lane. Ids come from the route, never the body. */
export type VendorLaneInput = Omit<VendorLane, 'laneId' | 'warehouseId'> & { warehouseId?: string };

/** Adds a lane to a vendor. Returns the vendor's full lane list afterwards. */
export const createVendorLane = async (vendorId: string, input: VendorLaneInput): Promise<VendorLane[]> =>
    ReparoApi<VendorLane[]>({
        method: 'POST',
        url: `/vendors/${encodeURIComponent(vendorId)}/lanes`,
        body: JSON.stringify(input),
    });

/** Edits one of a vendor's lanes by id: lead times, route, or the container cap. */
export const updateVendorLane = async (
    vendorId: string,
    laneId: string,
    input: VendorLaneInput,
): Promise<VendorLane> =>
    ReparoApi<VendorLane>({
        method: 'PATCH',
        url: `/vendors/${encodeURIComponent(vendorId)}/lanes/${encodeURIComponent(laneId)}`,
        body: JSON.stringify(input),
    });

// ─── Payment terms (vendors.payment_terms_config) ───────────────

export type VendorTermsTemplate = {
    label: string;
    type: PaymentTrancheType;
    percentage: number;
    anchor: PaymentAnchor;
    offsetDays?: number;
};

/** One named set of terms a vendor offers ("60-day credit"). */
export type VendorTermsOption = {
    id: string;
    name: string;
    /** Market tag. Absent = offered on every order to this vendor. */
    country?: string | null;
    isDefault?: boolean;
    /** Offered only on an order at least this large (order currency / units). Absent = any size. */
    minOrderValue?: number | null;
    minOrderQty?: number | null;
    tranches: VendorTermsTemplate[];
};

export type VendorPaymentTermsConfig = {
    options?: VendorTermsOption[];
    /**
     * The shape from before a vendor could offer more than one set of terms. Still read
     * (`listVendorTermsOptions` turns them into options), no longer written.
     */
    default?: VendorTermsTemplate[];
    byDestination?: Array<{ country: string; tranches: VendorTermsTemplate[] }>;
    /** Credit-partner rows only. */
    financing?: { creditDays: number; premiumPct: number };
};

export type VendorCapability = 'FG_SUPPLIER' | 'RM_SUPPLIER' | 'MANUFACTURER' | 'FREIGHT_FORWARDER';

export const VENDOR_CAPABILITY_LABEL: Record<VendorCapability, string> = {
    FG_SUPPLIER: 'Finished goods supplier',
    RM_SUPPLIER: 'RM / PM supplier',
    MANUFACTURER: 'Manufacturer',
    FREIGHT_FORWARDER: 'Freight forwarder',
};

/** Who can move a shipment for us. Tagged forwarders only - an untagged vendor is not one. */
export const isFreightForwarder = (v: { capabilities?: VendorCapability[] | null }): boolean =>
    (v.capabilities ?? []).includes('FREIGHT_FORWARDER');

/** Sets a vendor's capabilities outright (what it does for the brand). */
export const updateVendorCapabilities = async (
    vendorId: string,
    capabilities: VendorCapability[],
): Promise<void> => {
    await ReparoApi<any>({
        method: 'PATCH',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
        body: JSON.stringify({ capabilities }),
    });
};

/** What a vendor PATCH may carry. Every field is optional; the ones left out are untouched. */
export type UpdateVendorPayload = Partial<Omit<CreateVendorPayload, 'vendorId'>> & {
    paymentTermsConfig?: VendorPaymentTermsConfig | null;
    dailyProductionCapacity?: number | null;
    workingDays?: number[] | null;
};

/** Sets (or clears, with null) the PO number prefix a new order to this vendor starts from. */
export const updateVendorPoNumberPrefix = async (
    vendorId: string,
    poNumberPrefix: string | null,
): Promise<void> => {
    await ReparoApi<any>({
        method: 'PATCH',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
        body: JSON.stringify({ poNumberPrefix } satisfies UpdateVendorPayload),
    });
};

export type VendorListItem = {
    vendorId: string;
    name: string;
    vendorRole?: string | null;
    /** Null/empty = untagged; every picker keeps untagged vendors visible. */
    capabilities?: VendorCapability[] | null;
    paymentTermsConfig?: VendorPaymentTermsConfig | null;
};

export const listVendors = async (): Promise<VendorListItem[]> =>
    ReparoApi<any>({ method: 'GET', url: '/vendors' });

/**
 * The manufacturers that run a site in the portal, and where each one is emailed.
 *
 * Exists for the notification settings screen, which promises the manufacturer is copied "at
 * the address on its supplier record" and otherwise cannot say which — there is one address per
 * manufacturer, and which is used depends on who acknowledged.
 *
 * Narrowed by having a site rather than by the MANUFACTURER tag: only a vendor with a site can
 * be given a login, and the tag does not track that.
 */
export type PortalSiteVendor = {
    vendorId: string;
    name: string;
    /**
     * The vendor's contact address. Null means none on file, and nobody would be copied.
     *
     * Kept apart from the secondary because only this one is editable: `email` is a core
     * column on the vendors grid and `secondaryEmail` is not.
     */
    email: string | null;
    /** Copied too, but read-only wherever the primary can be edited. */
    secondaryEmail: string | null;
    /** False = a site nobody can log into, so this address will never be written to. */
    hasLogin: boolean;
};

export const listPortalSiteVendors = async (): Promise<PortalSiteVendor[]> =>
    ReparoApi<any>({ method: 'GET', url: '/vendors/portal-sites' });

/** Who can be bought from: FG / RM suppliers. A pure manufacturer is not a purchase vendor. */
export const isSupplierVendor = (v: { capabilities?: VendorCapability[] | null }): boolean => {
    const caps = v.capabilities ?? [];
    return caps.length === 0 || caps.includes('FG_SUPPLIER') || caps.includes('RM_SUPPLIER');
};

/** Who can make for us: manufacturers (a supplier who also manufactures qualifies). */
export const isManufacturerVendor = (v: { capabilities?: VendorCapability[] | null }): boolean => {
    const caps = v.capabilities ?? [];
    return caps.length === 0 || caps.includes('MANUFACTURER');
};

/** Channel parties kept as vendors (type SALES_VENDOR) — the "to party" of warehouse → channel transfers. */
export const listSalesVendors = async (): Promise<VendorListItem[]> =>
    ReparoApi<any>({ method: 'GET', url: '/vendors?type=SALES_VENDOR' });

/** PATCH just the terms config; every other vendor field is left untouched. */
export const updateVendorPaymentTerms = async (
    vendorId: string,
    paymentTermsConfig: VendorPaymentTermsConfig | null,
): Promise<void> => {
    await ReparoApi<any>({
        method: 'PATCH',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
        body: JSON.stringify({ paymentTermsConfig }),
    });
};

/**
 * ADMIN only, and merchant-scoped by the authenticated session — the owning
 * organisation is taken from the token, never from this payload.
 */
export const createVendor = async (
    payload: CreateVendorPayload,
): Promise<Vendor> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/vendors',
        body: JSON.stringify(payload),
    });
};

export type VendorProductPick = {
    productId: string;
    sku: string | null;
    name: string;
    /** The agreed rate, when one has been recorded. Zero is a real price, null is no price. */
    price: number | null;
    currency: string | null;
    /** The product's GST slab. Null means no rate stated — NOT the exempt 0% slab. */
    gstRate: number | null;
    /** HSN/SAC, so a picked line can show on the preview what the document will print. */
    hsnCode: string | null;
    /** Units in one case, so a picked line can be typed in cases. Null = units only. */
    unitsPerCase: number | null;
};

/**
 * What this manufacturer has been mapped to make.
 *
 * Feeds the product picker when authoring a production order, and carries the terms with it so
 * a picked line can fill in its own rate and slab.
 */
export const listProductsForVendor = async (
    vendorId: string,
): Promise<VendorProductPick[]> =>
    ReparoApi<any>({
        method: 'GET',
        url: `/products/vendor-mappings/${encodeURIComponent(vendorId)}`,
    });

export type VendorMaterialPick = {
    materialId: string;
    /** The agreed cost per unit. Null only when the row has none. */
    cost: number | null;
    /** ISO code `cost` is in. Null is the brand's own currency. */
    currency: string | null;
};

/**
 * What this supplier has been mapped to supply on the material side, with the agreed cost.
 *
 * Only ids, costs and their currency: the purchase-order form already holds the material catalogue and joins
 * on material id for the name, type and slab. The decimal arrives as a string, hence the
 * Number().
 */
export const listMaterialsForVendor = async (vendorId: string): Promise<VendorMaterialPick[]> => {
    const rows = await ReparoApi<Array<{ materialId: string; cost: string | number | null; currency: string | null }>>({
        method: 'GET',
        url: `/vendor-material-mappings?vendorId=${encodeURIComponent(vendorId)}`,
    });
    return (rows ?? []).map((r) => ({
        materialId: r.materialId,
        cost: r.cost == null ? null : Number(r.cost),
        currency: r.currency ?? null,
    }));
};

/** The suppliers mapped to supply this material, by vendor id. */
export const listVendorIdsForMaterial = async (materialId: string): Promise<string[]> => {
    const rows = await ReparoApi<Array<{ vendorId: string }>>({
        method: 'GET',
        url: `/vendor-material-mappings?materialId=${encodeURIComponent(materialId)}`,
    });
    return [...new Set((rows ?? []).map((r) => r.vendorId))];
};

/**
 * Says that this vendor supplies this material. The merchant is the caller's own. Safe to
 * repeat: a pair already mapped comes back as it is, its agreed cost untouched.
 */
export const createVendorMaterialMapping = async (payload: {
    materialId: string;
    vendorId: string;
    /** Optional — left out, the mapping carries no rate rather than a rate of zero. */
    cost?: number;
    /** ISO code the cost is in. Left out, the cost is in the brand's own currency. */
    currency?: string;
}): Promise<{ materialId: string; vendorId: string }> =>
    ReparoApi<any>({
        method: 'POST',
        url: '/vendor-material-mappings',
        body: JSON.stringify(payload),
    });

/** Removes one material mapping by its row id (the grid's `_rowId`). 404 if it is not this merchant's. */
export const deleteVendorMaterialMapping = async (rowId: string): Promise<void> => {
    await ReparoApi<any>({
        method: 'DELETE',
        url: `/vendor-material-mappings/${encodeURIComponent(rowId)}`,
    });
};

/** CSV columns the material mapping importer understands, in template order. */
export const VENDOR_MATERIAL_MAPPING_CSV_COLUMNS = [
    'material_id',
    'vendor_id',
    'cost',
    'currency',
] as const;

/** Of those, the ones a row cannot omit. */
export const VENDOR_MATERIAL_MAPPING_CSV_REQUIRED_COLUMNS = [
    'material_id',
    'vendor_id',
] as const;

/**
 * ADMIN only. The material-side twin of `bulkUpsertVendorMappings`: a pair that already
 * exists has its cost and currency updated. `code` and `vendor_code` are accepted in place
 * of the id columns.
 */
export const bulkUpsertVendorMaterialMappings = async (
    file: File,
    dryRun: boolean,
): Promise<MappingImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/vendor-material-mappings/bulk-upsert?dryRun=${dryRun}`,
        body: formData,
    });
};

export type CreateProductVendorMappingPayload = {
    productId: string;
    vendorId: string;
    /** Optional — a mapping is useful before a price has been agreed. */
    price?: number;
    currency?: string;
};

/**
 * ADMIN only. Create-only: an existing pair rejects with 409 rather than silently
 * overwriting a price somebody negotiated.
 */
export const createProductVendorMapping = async (
    payload: CreateProductVendorMappingPayload,
): Promise<{ productId: string; vendorId: string }> => {
    return ReparoApi<any>({
        method: 'POST',
        url: '/products/vendor-mappings',
        body: JSON.stringify(payload),
    });
};

/** What both mapping importers report back; `sku` names a product row, `code` a material one. */
export type MappingImportResult = {
    totalRows: number;
    created: number;
    updated: number;
    skipped: number;
    errors: Array<{ row: number; reason: string; sku?: string; code?: string }>;
    dryRun: boolean;
};

/** CSV columns the mapping importer understands, in template order. */
export const VENDOR_MAPPING_CSV_COLUMNS = [
    'product_id',
    'vendor_id',
    'price',
    'currency',
] as const;

/** Of those, the ones a row cannot omit. */
export const VENDOR_MAPPING_CSV_REQUIRED_COLUMNS = [
    'product_id',
    'vendor_id',
] as const;

/**
 * ADMIN only. Upserts rather than create-only: a pair that already exists has its
 * price and currency updated, because filling prices in on existing mappings is
 * the main reason to upload this file at all.
 *
 * `sku` and `vendor_code` are accepted in place of the id columns.
 */
/**
 * The price-list upload, one row per price line. `item_type` defaults to product. `min_qty`
 * is the tier floor (blank = any quantity); `payment_terms` is one of the vendor's terms
 * option names (blank = any terms). The importer reports what it would do on a dry run.
 */
export const VENDOR_PRICE_CSV_COLUMNS = [
    'item_type',
    'item_id',
    'vendor_id',
    'price',
    'currency',
    'min_qty',
    'payment_terms',
    'effective_from',
    'reference',
    'note',
] as const;
export const VENDOR_PRICE_CSV_REQUIRED_COLUMNS = ['item_id', 'vendor_id', 'price'] as const;

/** Loads a price list; the importer's own counts are folded into the shape the import modal renders. */
export const uploadVendorPrices = async (
    file: File,
    dryRun: boolean,
): Promise<MappingImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const r = await ReparoApi<{
        totalRows: number;
        created: number;
        versioned: number;
        corrected: number;
        unchanged: number;
        superseded: number;
        errors: Array<{ row: number; reason: string; itemId?: string }>;
        dryRun: boolean;
    }>({
        method: 'POST',
        url: `/vendor-prices/upload?dryRun=${dryRun}`,
        body: formData,
    });
    return {
        totalRows: r.totalRows,
        created: r.created,
        updated: r.versioned + r.corrected,
        skipped: r.unchanged,
        errors: r.errors.map((e) => ({ row: e.row, reason: e.reason, sku: e.itemId })),
        dryRun: r.dryRun,
    };
};

export const bulkUpsertVendorMappings = async (
    file: File,
    dryRun: boolean,
): Promise<MappingImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    return ReparoApi<any>({
        method: 'POST',
        url: `/products/vendor-mappings/bulk-upsert?dryRun=${dryRun}`,
        body: formData,
    });
};

/**
 * How much a manufacturer can make: units per working day, and which weekdays it runs
 * (0 = Sunday … 6 = Saturday). Null capacity = unconstrained; null days = every day.
 * PATCHes just these two fields.
 */
export const updateVendorProductionCapacity = async (
    vendorId: string,
    input: { dailyProductionCapacity: number | null; workingDays: number[] | null },
): Promise<void> => {
    await ReparoApi<any>({
        method: 'PATCH',
        url: `/vendors/${encodeURIComponent(vendorId)}`,
        body: JSON.stringify(input),
    });
};

/** One order line to price: what is bought and how many. */
export type AgreedPriceRequestLine = {
    itemType: 'product' | 'material';
    itemId: string;
    /** Units on the line; tiers are judged per line. Left out = the first tier. */
    quantity?: number;
};

/** The agreed price for one line, for its quantity, on the order's terms and in its currency. */
export type AgreedLinePrice = {
    itemType: 'product' | 'material';
    itemId: string;
    /** Null when nothing applies: no price, none on these terms, or none in this currency. */
    price: number | null;
    currency: string | null;
    /** Tier floor of the line used; 0 = any quantity. */
    minQty: number | null;
    /** '' = a price that holds on any terms. */
    termsOptionId: string | null;
    termsName: string | null;
    /** The quantity is under the vendor's lowest tier; `price` is that tier's. */
    belowMinimum: boolean;
    minimum: number | null;
    /** Set when nothing is agreed in the order's currency but something is in these. */
    otherCurrencies: string[];
};

/**
 * The agreed price of each line of an order being drafted, from one vendor, on the order's
 * payment terms (else the vendor's default) and in its currency. One request for all lines.
 */
export const resolveAgreedPrices = async (body: {
    vendorId: string;
    currency?: string;
    termsOptionId?: string;
    lines: AgreedPriceRequestLine[];
}): Promise<AgreedLinePrice[]> =>
    ReparoApi<AgreedLinePrice[]>({
        method: 'POST',
        url: '/vendor-prices/resolve',
        body: JSON.stringify(body),
    });

/** One line of the vendor price list, as the server returns it. */
export type VendorPriceLine = {
    id: string;
    vendorId: string;
    itemType: 'product' | 'material';
    itemId: string;
    currency: string;
    unit: string;
    /** Tier floor; 0 = any quantity. */
    minQty: number;
    /** '' = holds on any terms. */
    termsOptionId: string;
    price: number;
    effectiveFrom: string;
    effectiveTo: string | null;
    note: string | null;
};

/** The current price lines for one vendor, optionally narrowed to one item. */
export const listVendorPriceLines = async (args: {
    vendorId: string;
    itemType?: 'product' | 'material';
    itemId?: string;
}): Promise<VendorPriceLine[]> => {
    const params = new URLSearchParams({ vendorId: args.vendorId });
    if (args.itemType) params.set('itemType', args.itemType);
    if (args.itemId) params.set('itemId', args.itemId);
    return ReparoApi<VendorPriceLine[]>({ method: 'GET', url: `/vendor-prices?${params.toString()}` });
};

/**
 * Records one agreed price (admin only). A new combination of vendor, item, currency, tier
 * and terms adds a line; an existing one is a price change, so the current line ends on the day
 * this one starts and the old price stays in the history.
 */
export const setVendorPrice = async (body: {
    vendorId: string;
    itemType: 'product' | 'material';
    itemId: string;
    price: number;
    currency: string;
    minQty: number;
    termsOptionId?: string;
    effectiveFrom?: string;
    note?: string;
}): Promise<VendorPriceLine> =>
    ReparoApi<VendorPriceLine>({
        method: 'POST',
        url: '/vendor-prices',
        body: JSON.stringify(body),
    });
